import React, { useState, useRef, forwardRef, useEffect } from 'react';
import { Box, TextField, IconButton } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

// Import Close icon from our icon system
import { Delete, Visible, Invisible } from '../../assets/images';

const StyledInputField = styled(TextField, {
    shouldForwardProp: (prop) => prop !== 'emptydate',
})(({ theme, inputtype }) => ({
    '& .MuiOutlinedInput-root': {
        backgroundColor:
            theme.palette.figma.white[10],
        borderRadius: '15px',
        height: '40px',
        padding: '0 15px',
        [theme.breakpoints.up('md')]: {
            height: '48px',
            padding: '0 18px',
            borderRadius: '12px',
        },
        '& fieldset': {
            border: 'none',
        },
        '& input': {
            color: theme.palette.text.main,
            fontSize: '14px',
            fontWeight: 500,
            letterSpacing: '-0.42px',
            padding: '0',
            [theme.breakpoints.up('md')]: {
                fontSize: '15px',
                letterSpacing: '-0.3px',
            },
            '&::placeholder': {
                color: theme.palette.figma.white[40],
                opacity: 1,
            },
            // 날짜 타입일 때 브라우저 기본 아이콘 숨기기
            ...(inputtype === 'date' && {
                '&::-webkit-calendar-picker-indicator': {
                    display: 'none',
                },
                '&::-webkit-inner-spin-button': {
                    display: 'none',
                },
                '&::-webkit-clear-button': {
                    display: 'none',
                },
            }),
            // 날짜 타입: 앱 로케일 포맷을 오버레이로 보여주므로, 네이티브 입력 텍스트는 숨김(브라우저 언어 무시)
            ...(inputtype === 'date' && {
                color: 'transparent',
                caretColor: theme.palette.text.main,
                '&::-webkit-datetime-edit': { color: 'transparent' },
                '&::-webkit-datetime-edit-text': { color: 'transparent' },
                '&::-webkit-datetime-edit-month-field': { color: 'transparent' },
                '&::-webkit-datetime-edit-day-field': { color: 'transparent' },
                '&::-webkit-datetime-edit-year-field': { color: 'transparent' },
            }),
        },
    },
    // helperText가 있든 없든 일정한 높이 유지
    '& .MuiFormHelperText-root': {
        margin: '4px 0 0 0',
        fontSize: '10px',
        lineHeight: '10px',
        [theme.breakpoints.up('md')]: {
            fontSize: '12px',
            lineHeight: '14px',
        },
    },
    // 전체 컴포넌트 높이 고정
    '&.MuiFormControl-root': {
        // minHeight: '60px', // input(40px) + helperText(10px)
    },
}));

const InputField = forwardRef(({
    value = '',
    placeholder = '입력하세요.',
    type = 'text',
    showClearIcon = true,
    showVisibilityIcon = false,
    onChange = () => { },
    onClear = () => { },
    onToggleVisibility = () => { },
    disabled = false,
    error = false,
    helperText = '',
    maxLength = null,
    customEndAdornment = null,
    inputProps: customInputProps = {},
    sx = {},
    ...props
}, ref) => {
    const theme = useTheme();
    const { i18n, t } = useTranslation();
    const [showPassword, setShowPassword] = useState(false);
    const [internalValue, setInternalValue] = useState(value);

    // 날짜 값을 YYYY-MM-DD 형식으로 정규화
    const normalizeDateValue = (dateValue) => {
        if (!dateValue || type !== 'date') return dateValue;
        // 점(.)이나 슬래시(/)를 하이픈(-)으로 변환
        return dateValue.replace(/[.\/]/g, '-');
    };

    // value prop이 변경될 때 internalValue 동기화
    useEffect(() => {
        if (value !== undefined) {
            const normalized = normalizeDateValue(value);
            setInternalValue(normalized);
        }
    }, [value, type]);

    const handleChange = event => {
        let newValue = event.target.value;

        // 날짜 타입일 때 점(.)을 하이픈(-)으로 변환하여 YYYY-MM-DD 형식 유지
        if (type === 'date' && newValue) {
            // 점으로 구분된 날짜를 하이픈으로 변환 (예: 2024.01.01 -> 2024-01-01)
            newValue = newValue.replace(/\./g, '-');
            // 하이픈이 아닌 다른 구분자도 하이픈으로 변환
            newValue = newValue.replace(/[\/\s]/g, '-');
        }

        setInternalValue(newValue);

        // 변환된 값을 이벤트에 반영
        const modifiedEvent = {
            ...event,
            target: {
                ...event.target,
                value: newValue
            }
        };
        onChange(modifiedEvent);
    };

    const handleClear = () => {
        setInternalValue('');
        // onChange 이벤트를 발생시켜서 부모 컴포넌트의 상태도 업데이트
        const event = {
            target: {
                value: ''
            }
        };
        onChange(event);
        onClear();
    };

    const handleToggleVisibility = () => {
        setShowPassword(!showPassword);
        onToggleVisibility(!showPassword);
    };

    // value가 숫자 타입이면 문자열로 변환, 날짜 타입이면 정규화
    const normalizedValue = value !== undefined
        ? (typeof value === 'number' ? String(value) : (type === 'date' ? normalizeDateValue(value) : value))
        : undefined;
    const currentValue = normalizedValue !== undefined ? normalizedValue : (type === 'date' ? normalizeDateValue(internalValue) : internalValue);
    const inputtype = type === 'password' && showPassword ? 'text' : type;

    // 날짜 타입일 때 placeholder (연도. 월. 일. / Year. Month. Day. 등)
    const datePlaceholder = type === 'date' ? t('date_placeholder_year_month_day') : placeholder;

    // type="date"일 때 MUI X DatePicker 사용 → 캘린더 팝업 UI가 앱 로케일(LocalizationProvider)로 표시됨
    const datePickerValue = type === 'date'
        ? (currentValue && dayjs(currentValue).isValid() ? dayjs(currentValue) : null)
        : undefined;

    const handleDatePickerChange = (newValue) => {
        const str = newValue && dayjs(newValue).isValid() ? dayjs(newValue).format('YYYY-MM-DD') : '';
        setInternalValue(str);
        onChange({ target: { value: str } });
    };

    const dateFieldSx = {
        '& .MuiOutlinedInput-root': {
            backgroundColor: theme.palette.figma.white[10],
            borderRadius: '15px',
            height: '40px',
            padding: '0 15px',
            [theme.breakpoints.up('md')]: {
                height: '48px',
                padding: '0 18px',
                borderRadius: '12px',
            },
            '& fieldset': { border: 'none' },
            '& input': {
                color: theme.palette.text.main,
                fontSize: '14px',
                fontWeight: 500,
                letterSpacing: '-0.42px',
                padding: '0',
                [theme.breakpoints.up('md')]: {
                    fontSize: '15px',
                    letterSpacing: '-0.3px',
                },
            },
        },
        '& .MuiFormHelperText-root': {
            margin: '4px 0 0 0',
            fontSize: '10px',
            lineHeight: '10px',
            [theme.breakpoints.up('md')]: {
                fontSize: '12px',
                lineHeight: '14px',
            },
        },
    };

    if (type === 'date') {
        return (
            <Box
                sx={{
                    position: 'relative',
                    width: '100%',
                    maxWidth: { md: 420 },
                    alignSelf: { md: 'center' },
                    mx: { md: 'auto' },
                    minHeight: { xs: '40px', md: '48px' },
                    ...sx,
                }}
            >
                <DatePicker
                    ref={ref}
                    value={datePickerValue}
                    onChange={handleDatePickerChange}
                    disabled={disabled}
                    clearable={showClearIcon}
                    onClear={handleClear}
                    placeholder={datePlaceholder}
                    slotProps={{
                        textField: {
                            fullWidth: true,
                            error,
                            helperText,
                            placeholder: datePlaceholder,
                            sx: dateFieldSx,
                            ...props,
                        },
                        // 캘린더(열기) 아이콘 버튼 색상
                        openPickerButton: {
                            sx: {
                                color: theme.palette.figma.icon,
                                '&:hover': { backgroundColor: 'transparent' },
                            },
                        },
                        // 데스크톱: 캘린더 팝업 배경 (Popup 컴포넌트와 동일)
                        desktopPaper: {
                            sx: {
                                background: theme.palette.figma.popupBG,
                                borderRadius: '20px',
                            },
                        },
                        // 모바일: 백드롭 + 다이얼로그 배경 (Popup과 동일)
                        dialog: {
                            sx: {
                                '& .MuiBackdrop-root': {
                                    backgroundColor: theme.palette.figma.white[80],
                                },
                            },
                        },
                        mobilePaper: {
                            sx: {
                                background: theme.palette.figma.popupBG,
                                borderRadius: '20px',
                            },
                        },
                    }}
                />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                position: 'relative',
                width: '100%',
                maxWidth: { md: 420 },
                alignSelf: { md: 'center' },
                mx: { md: 'auto' },
                minHeight: { xs: '40px', md: '48px' },
                ...sx,
            }}
        >
            <StyledInputField
                ref={ref}
                fullWidth
                value={currentValue}
                placeholder={placeholder}
                type={inputtype}
                inputtype={inputtype}
                onChange={handleChange}
                disabled={disabled}
                error={error}
                helperText={helperText}
                inputProps={{ maxLength: maxLength, ...customInputProps }}
                InputProps={{
                    endAdornment: (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '5px',
                                minWidth:
                                    showClearIcon || showVisibilityIcon || customEndAdornment
                                        ? '24px'
                                        : 0,
                                justifyContent: 'flex-end',
                            }}
                        >
                            {showClearIcon && currentValue && !disabled && (
                                <IconButton
                                    size="small"
                                    onClick={handleClear}
                                    sx={{
                                        padding: '2px',
                                        color: theme.palette.figma.icon,
                                        '&:hover': {
                                            backgroundColor:
                                                theme.palette.figma.white[10]
                                        },
                                    }}
                                >
                                    <Delete
                                        size={20}
                                        color={theme.palette.figma.icon}
                                    />
                                </IconButton>
                            )}
                            {showVisibilityIcon && type === 'password' && (
                                <IconButton
                                    size="small"
                                    onClick={handleToggleVisibility}
                                    sx={{
                                        padding: '2px',
                                        color: theme.palette.figma.icon,
                                        '&:hover': {
                                            backgroundColor:
                                                theme.palette.figma.white[10],
                                        },
                                    }}
                                >
                                    {showPassword ? (
                                        <Invisible
                                            size={20}
                                            color={theme.palette.figma.icon}
                                        />
                                    ) : (
                                        <Visible
                                            size={20}
                                            color={theme.palette.figma.icon}
                                        />
                                    )}
                                </IconButton>
                            )}
                            {customEndAdornment && !currentValue && customEndAdornment}
                        </Box>
                    ),
                }}
                {...props}
            />
        </Box>
    );
});

InputField.displayName = 'InputField';

export default InputField;
